import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WalletSelectorDialogService } from '@frontend/sports/account-balance/feature';
import { DialogSettings } from '@frontend/sports/modal/feature';
import { Store } from '@ngrx/store';
import { PopupManager } from 'packages/sports/web/app/src/popup/popup-manager.service';
import { IUiRootState } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { filter, pairwise, take, takeWhile, tap } from 'rxjs';

import { IBetslipRootState } from '../../base/store/state';
import { betslipComponentSelector } from '../../digital/components/betslip/betslip.component';

@Component({
    selector: 'bs-wallet-type-container',
    templateUrl: './wallet-type-container.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class WalletTypeContainerComponent implements OnInit {
    @Input() inColumn = false;
    @Input() showExtendedInfo?: boolean;
    @Input() singleWallet = false;
    @Input() trackingPosition: string;
    @Input() bottomOptions = false;
    setEmptyBetslipStyle = false;
    setExpandedEmptyBetslipStyle = false;
    private readonly destroyRef = inject(DestroyRef);
    private readonly _doc = inject(DOCUMENT);

    constructor(
        private store: Store<IBetslipRootState & IUiRootState>,
        private walletSelectorDialogService: WalletSelectorDialogService,
        private popupManager: PopupManager,
    ) {}

    ngOnInit() {
        this.tryOpenOnInit();
        this.toggleOnPicksChanges();
    }

    private toggleOnPicksChanges() {
        this.store
            .select(betslipComponentSelector)
            .pipe(
                takeWhile((_) => this.walletSelectorDialogService.showWalletSelector === true),
                pairwise(),
                filter(
                    ([previous, current]) =>
                        (previous.picksCount === 0 && current.picksCount >= 1) || (previous.picksCount >= 1 && current.picksCount === 0),
                ),
                tap(([previous, current]) => {
                    if (!this.popupManager.isOpen() && !this.inColumn) {
                        return;
                    }

                    if (previous.picksCount === 0 && current.picksCount >= 1) {
                        const modalDialogContainer = this._doc.querySelector<HTMLElement>('.column-right .widget-slot') || void 0;
                        this.openPopup(modalDialogContainer);
                    } else {
                        this.walletSelectorDialogService.close();
                    }
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    onExpandWalletDropDown = (expand: boolean): void => {
        this.setEmptyBetslipStyle = expand;
    };

    onOpenPayPalHelp = (openPayPalHelp: boolean): void => {
        this.setExpandedEmptyBetslipStyle = openPayPalHelp;
    };

    //Since betslip in popup is destroyed and re-created everytime, so openOnPicksChanges isn't going to work, hence the workaround below.
    private tryOpenOnInit() {
        if (this.walletSelectorDialogService.showWalletSelector && !this.inColumn) {
            this.store
                .select(betslipComponentSelector)
                .pipe(
                    filter(({ picksCount }) => picksCount > 0),
                    take(1),
                )
                .subscribe((_) => this.openPopup());
        }
    }

    private openPopup(container?: HTMLElement | undefined) {
        const settings: DialogSettings = {
            fit: this.inColumn,
            inColumn: this.inColumn,
            cssClass: 'betslip-vars-scope wallet-selector-mobile-betslip',
            container,
        };

        this.walletSelectorDialogService.open(settings, this.inColumn);
    }
}
